import { MetricTile, StatusPill } from "../BisWidgets";
import { hasAnyText } from "../bisDossierData";
import {
    activeDossierHeaderStyle,
    activeDossierMetaGridStyle,
    activeDossierShellStyle,
    activeDossierTopRowStyle,
    controleStepDetailStyle,
    controleStepNumberStyle,
    controleTabButtonStyle,
    controleTabsStyle,
    saveDossierButtonStyle,
    validateDossierButtonStyle,
} from "../bisLayoutStyles";
import {
    productionProgressFillStyle,
    productionProgressLabelStyle,
    productionProgressPanelStyle,
    productionProgressTrackStyle,
    productionProgressValueStyle,
} from "../bisProductionStyles";
import { renderDossierList } from "./DossierListView";
import { renderPostProductionTab } from "./PostProductionTabView";
import { renderPreProductionTab } from "./PreProductionTabView";
import { renderProductionDossierLotTab } from "./ProductionDossierLotTabView";
import { controleModeLabel, isAppleKeyboard, saveShortcutLabel } from "../../shared/controleConstants";
import { formatDate } from "../../shared/controleFormat";
import { controleTabsRowStyle, shortcutPillStyle } from "../../shared/styles/layoutStyles";

// Avancement de chaque étape du dossier : pré-production (patients validés), dossier de lot (fiche terminée ou en
// cours) et post-production (pièces ou réparation saisies).
export function dossierProgress({ activeControlePatientCount, validatedPatientCount, workspace }) {
    const { activeDossier, formData } = workspace;
    const preProduction = activeControlePatientCount ? Math.round((validatedPatientCount / activeControlePatientCount) * 100) : 0;
    const dossierLot = activeDossier.status === "TERMINE" ? 100 : 50;
    const postProductionAttachments = (formData.piecesJointes || []).filter(attachment => attachment.section !== "preprodPoint6");
    const postProduction = postProductionAttachments.length > 0 || hasAnyText(Object.values(formData.reparationControleFinal || {})) ? 50 : 0;
    return Math.round((preProduction + dossierLot + postProduction) / 3);
}

function workflowSteps({ activeControlePatientCount, controle, validatedPatientCount, workspace }) {
    const documentCount = (workspace.formData.piecesJointes || []).length;
    return [
        { key: "controlePatient", label: "Pré-production", detail: `${validatedPatientCount}/${activeControlePatientCount || controle.patients.length} patients` },
        { key: "dossierLot", label: "Dossier de lot", detail: workspace.activeDossier.status === "TERMINE" ? "Fiche terminée" : "Saisie en cours" },
        { key: "postProduction", label: "Post-production", detail: `${documentCount} document${documentCount > 1 ? "s" : ""}` },
    ];
}

function BisDossierTitle({ view }) {
    const { activeControleMode, saveDossier, workspace } = view;
    return (
        <div style={activeDossierTopRowStyle}>
            <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <h2 style={{ margin: 0, fontSize: "20px", color: "#0f172a", letterSpacing: 0 }}>Dossier de lot {workspace.activeDossier.numeroLot}</h2>
                    <StatusPill status={workspace.activeDossier.status} />
                </div>
                <div style={{ color: "#64748b", fontSize: "13px", marginTop: "4px" }}>
                    {workspace.activeDossier.ehpadNom || workspace.selectedEhpad?.nom} · Production du {formatDate(workspace.activeDossier.dateProduction)} · Mode {controleModeLabel(activeControleMode)}
                </div>
            </div>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button
                    type="button"
                    disabled={workspace.saving}
                    onClick={() => saveDossier("BROUILLON")}
                    style={{ ...saveDossierButtonStyle, opacity: workspace.saving ? 0.65 : 1 }}
                    title={`Enregistrer la fiche (${saveShortcutLabel})`}
                    aria-keyshortcuts={isAppleKeyboard ? "Meta+S" : "Control+S"}
                >
                    <span>Enregistrer</span>
                    <span style={shortcutPillStyle}>{saveShortcutLabel}</span>
                </button>
                <button
                    type="button"
                    disabled={workspace.saving}
                    onClick={() => saveDossier("TERMINE")}
                    style={{ ...validateDossierButtonStyle, opacity: workspace.saving ? 0.65 : 1 }}
                >
                    Valider la fiche
                </button>
            </div>
        </div>
    );
}

function BisWorkflowTabs({ view }) {
    const { activeControleTab, setActiveControleTab } = view;
    return (
        <div style={{ ...controleTabsRowStyle, marginTop: "14px" }}>
            <div style={controleTabsStyle}>
                {workflowSteps(view).map((step, index) => (
                    <button
                        key={step.key}
                        type="button"
                        onClick={() => setActiveControleTab(step.key)}
                        title={step.detail}
                        style={controleTabButtonStyle(activeControleTab === step.key)}
                    >
                        <span style={controleStepNumberStyle(activeControleTab === step.key)}>{index + 1}</span>
                        <span style={{ display: "grid", gap: "2px", textAlign: "left" }}>
                            <span>{step.label}</span>
                            <small style={controleStepDetailStyle(activeControleTab === step.key)}>{step.detail}</small>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

function BisDossierHeader({ view }) {
    const { activeControlePatientCount, blockingPatientIssues, controle, remainingPatientCount, validatedPatientCount } = view;
    const globalProgress = dossierProgress(view);
    return (
        <div style={activeDossierHeaderStyle}>
            <BisDossierTitle view={view} />
            <div style={{ ...productionProgressPanelStyle, marginTop: "18px", boxShadow: "none" }}>
                <span style={productionProgressLabelStyle}>Progression globale</span>
                <span style={productionProgressTrackStyle}>
                    <span style={{ ...productionProgressFillStyle, width: `${globalProgress}%` }} />
                </span>
                <strong style={productionProgressValueStyle}>{globalProgress}%</strong>
            </div>
            <BisWorkflowTabs view={view} />
            <div style={activeDossierMetaGridStyle}>
                <MetricTile label="Patients validés" value={`${validatedPatientCount}/${activeControlePatientCount || controle.patients.length}`} tone={remainingPatientCount > 0 ? "warning" : "success"} />
                <MetricTile label="À reprendre" value={blockingPatientIssues.length} tone={blockingPatientIssues.length ? "danger" : "success"} />
            </div>
        </div>
    );
}

export function renderActiveDossier(view) {
    const { activeControleTab, workspace } = view;
    if (!workspace.activeDossier) return renderDossierList(view);
    return (
        <div style={activeDossierShellStyle}>
            <BisDossierHeader view={view} />

            {activeControleTab === "controlePatient" && renderPreProductionTab(view)}

            {activeControleTab === "dossierLot" && renderProductionDossierLotTab(view)}

            {activeControleTab === "postProduction" && renderPostProductionTab(view)}

        </div>
    );
}
